import { useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { useViewingResource } from "src/features/notifications/hooks";

import axios from "axios";
import { useParams } from "react-router-dom";
import { nuclearn_api_url } from "src/App";
import { useGetDialogueConfig } from "src/reusable/Chat/ChatStreaming/services/api";
import { useGetDialogueById } from "../AssistantServices";
import { AtomAssistChatPageParams } from "../AtomAssistChatPage";
import { PersonaConfigurationService } from "../services/PersonaConfigurationService";
import { useChatStore } from "../stores/ChatStore";
import { attachSdrIdsFromStatus } from "./dialogueArtifactSdr";
import {
  fetchDocumentAttachments,
  savedDocumentIds,
  staleDocumentChipIds,
} from "./dialogueDocumentScope";
import {
  DEFAULT_RESPONSE_MODE,
  isResponseMode,
} from "src/reusable/Chat/types/chatTypes";

const useLoadDialogue = ({
  initialUserMessage,
  dialogueId: dialogueIdProp,
  restoreDocumentScope = false,
}: {
  initialUserMessage?: string;
  dialogueId?: number;
  /** Restore the dialogue's saved Chat with Document scope. AtomAssist only:
   *  product chats manage their own record scope. */
  restoreDocumentScope?: boolean;
}) => {
  // In the standalone /atom-assist/chat/:dialogueId route the id lives in the
  // URL. When AtomAssistPage is embedded inside ChatPanePanel there is no
  // matching URL segment, so the prop is the only source of truth.
  const { dialogueId: dialogueIdParam } = useParams<AtomAssistChatPageParams>();
  const dialogueId =
    dialogueIdProp !== undefined ? dialogueIdProp : Number(dialogueIdParam);
  // Every product chat loads through here, so this is where "the user is
  // looking at this dialogue" is declared: its status marker in chat lists
  // resets to idle and its bell entries are read, now and as turns finish.
  useViewingResource(
    "dialogue",
    Number.isFinite(dialogueId) ? dialogueId : null,
  );

  const {
    setActiveDialogueId,
    setAgentDatasetAccess,
    setConfig,
    setPersona,
    setPrivateDatasetDocumentsAsAttachments,
    setRouting,
    restoreResponseMode,
    setSourceDataRecordIds,
    addUploadedFile,
    removeUploadedFile,
  } = useChatStore();
  const queryClient = useQueryClient();

  // Track if we've already loaded this dialogue to prevent re-loading
  const loadedDialogueId = useRef<number | null>(null);
  const hasLoadedConfig = useRef(false);
  // The dialogue whose document scope was applied. Once per dialogue: a later
  // pass would overwrite records the chat added since, or clear a first turn's
  // scope in the gap before its config is saved.
  const documentScopeAppliedFor = useRef<number | null>(null);
  // When this dialogue became the current one. A cached copy from an earlier
  // visit predates it and may predate the last turn, so the scope is applied
  // only from data fetched since.
  const currentDialogue = useRef<{ id: number; since: number } | null>(null);
  if (currentDialogue.current?.id !== dialogueId) {
    currentDialogue.current = { id: dialogueId, since: Date.now() };
  }

  // Always enable dialogue query when we have a dialogueId, regardless of initialUserMessage
  // This ensures config is loaded on page reload
  const shouldEnableDialogueQuery =
    Number.isFinite(dialogueId) && dialogueId > 0;

  const {
    data: dialogue,
    isLoading: isDialogueLoading,
    isSuccess: isDialogueSuccess,
    isError: isDialogueError,
    error: dialogueError,
    dataUpdatedAt: dialogueUpdatedAt,
  } = useGetDialogueById({
    dialogueId: shouldEnableDialogueQuery ? dialogueId : null,
  });

  // Fetch dialogue config with full dataset objects (names, etc.)
  const {
    data: dialogueConfigWithDatasets,
    error: dialogueConfigError,
    dataUpdatedAt: dialogueConfigUpdatedAt,
  } = useGetDialogueConfig({
    dialogueId: shouldEnableDialogueQuery ? dialogueId : null,
  });

  // The dialogue and dialogue-config endpoints return 404 both for missing
  // dialogues and for other users' dialogues (deliberately indistinguishable —
  // backend #30212). Surface it so the page can show a "not found or no
  // access" message rather than a generic load error.
  const accessError = dialogueError ?? dialogueConfigError;
  const isNotFound =
    axios.isAxiosError(accessError) && accessError.response?.status === 404;

  // Set active dialogue ID and load dialogue data
  useEffect(() => {
    // Always load dialogue configuration when we have dialogue data
    // This ensures settings are restored on page reload
    if (dialogueId && isDialogueSuccess && dialogue) {
      // Mark this dialogue as loaded
      loadedDialogueId.current = dialogueId;

      // Set the active dialogue ID
      setActiveDialogueId(dialogueId);

      // Always apply dialogue config as the authoritative source when available
      // This ensures dialogue-config API response is the final state
      if (dialogue.config && dialogueConfigWithDatasets) {
        hasLoadedConfig.current = true;

        let mlmodelConfig = {};
        try {
          mlmodelConfig = JSON.parse(dialogue.mlmodel_config || "{}");
        } catch (e) {
          console.error("Failed to parse mlmodel_config", e);
        }

        // Separate datasets by visibility level AND identify project datasets
        const allDatasets = dialogueConfigWithDatasets?.datasets || [];
        const publicDatasets = allDatasets.filter(
          (dataset: any) => dataset.visibility_level !== "private",
        );
        const privateDatasets = allDatasets.filter(
          (dataset: any) => dataset.visibility_level === "private",
        );

        // Also identify project datasets regardless of visibility
        const projectDatasets = allDatasets.filter(
          (dataset: any) => !!dataset.name?.match(/^project_\d+_uploads$/i),
        );

        // Combine private datasets with project datasets (avoiding duplicates)
        const datasetsToFetchDocuments = [
          ...privateDatasets,
          ...projectDatasets.filter(
            (pd: any) =>
              !privateDatasets.some((priv: any) => priv.id === pd.id),
          ),
        ];

        // Unsent dialogues save agent/datasets early (uploads); the composer's handoff is newer.
        const hasStarted = (dialogue.messages?.length ?? 0) > 0;
        const { composerHandoff } = useChatStore.getState();
        const handoff =
          !hasStarted && composerHandoff?.dialogueId === dialogueId
            ? composerHandoff
            : null;

        // When loading from history, merge configs from both APIs
        // dialogue-config API response has the most complete data
        const newConfig = {
          ...dialogue.config,
          modelId: dialogue.model_fk,
          modelVersionNum:
            (mlmodelConfig as any).model_version_number ||
            dialogue.mlmodel_version_num ||
            1,
          // From the record, only public datasets go in the config/dropdown
          datasets: handoff
            ? (handoff.config.datasets ?? publicDatasets)
            : publicDatasets,
          chatConfigId: handoff
            ? handoff.config.chatConfigId
            : (dialogue.config.atom_assist_config_fk ?? undefined),
          // Always enable tool_calling
          toolCalling: true,
          // Merge top_search_results from both sources
          topSearchResults:
            dialogueConfigWithDatasets.top_search_results ??
            dialogue.config.top_search_results ??
            5,
          chatControlMetadata: {},
          // Identity fields (name, slug, etc.) belong in PersonaData, not ChatConfig.
          // setPersona() below handles persona identity for display.
        };

        // Apply config from dialogue-config API as final authority
        setConfig(newConfig);

        // A conversation with messages has its response mode fixed: adopt the
        // persisted one (Thorough for dialogues that predate modes) and lock.
        // A fresh dialogue keeps whatever the splash chose.
        if (hasStarted) {
          const persisted = dialogue.config.response_mode;
          restoreResponseMode(
            isResponseMode(persisted) ? persisted : DEFAULT_RESPONSE_MODE,
          );
        }

        // Fetch documents from private datasets AND project datasets
        if (datasetsToFetchDocuments.length > 0) {
          // Fetch artifacts/documents for each dataset
          Promise.all(
            datasetsToFetchDocuments.map(async (dataset: any) => {
              const artifactUrl = `${nuclearn_api_url}/datasets/${dataset.id}/get-dataset-artifacts-by-id`;

              try {
                const response = await axios.get(artifactUrl);

                // Check if this is a project dataset based on naming pattern
                const isProjectDataset = !!dataset.name?.match(
                  /^project_\d+_uploads$/i,
                );

                // Add dataset_id and project flag to each artifact for tracking
                const artifactsWithDatasetId = response.data.map(
                  (artifact: any) => ({
                    ...artifact,
                    dataset_id: dataset.id,
                    isFromProject: isProjectDataset, // Mark as project file if from project dataset (boolean)
                  }),
                );

                return artifactsWithDatasetId;
              } catch (error: any) {
                console.error(
                  "❌ [useLoadDialogue] Failed to fetch artifacts",
                  {
                    datasetId: dataset.id,
                    datasetName: dataset.name,
                    error: error.message,
                    status: error.response?.status,
                    timestamp: new Date().toISOString(),
                  },
                );
                return [];
              }
            }),
          )
            .then(async (results) => {
              // Flatten all artifacts from all private datasets
              const allDocuments = results.flat();

              if (allDocuments.length > 0) {
                // Fetch SDR IDs for documents before setting them as attachments
                // Group documents by dataset_id to fetch SDR IDs efficiently
                const documentsByDataset = allDocuments.reduce(
                  (acc, doc) => {
                    const datasetId = doc.dataset_id;
                    if (!acc[datasetId]) {
                      acc[datasetId] = [];
                    }
                    acc[datasetId].push(doc);
                    return acc;
                  },
                  {} as Record<number, any[]>,
                );

                // Fetch SDR IDs for all datasets
                for (const [datasetId, docs] of Object.entries(
                  documentsByDataset,
                )) {
                  try {
                    const statusResponse = await axios.get<any[]>(
                      `${nuclearn_api_url}/datasets/processing-status?dataset_ids=${datasetId}`,
                    );

                    if (statusResponse.data && statusResponse.data.length > 0) {
                      // Deterministic exact-match join (normalized filename ->
                      // SDR id). A doc whose name carries a storage prefix still
                      // joins, so it never silently misses and leaves the
                      // attachment SDR-less (which re-triggers the gating dead
                      // zone).
                      attachSdrIdsFromStatus(
                        docs as any[],
                        statusResponse.data,
                      );
                    }
                  } catch (error) {
                    console.error(
                      `Failed to fetch SDR IDs for dataset ${datasetId}:`,
                      error,
                    );
                  }
                }

                // Check if we already have these files (from sessionStorage rehydration)
                // to avoid duplicates when reloading the page
                const existingFiles = useChatStore.getState().uploadedFiles;

                // Create sets of existing file identifiers for efficient lookup
                // Check by artifact ID and source data record ID rather than just dataset ID
                const existingArtifactIds = new Set(
                  existingFiles
                    .filter((f: any) => f.artifactId)
                    .map((f: any) => String(f.artifactId)),
                );

                // Also check file IDs that might have the artifact- prefix
                const existingFileIds = new Set(
                  existingFiles.map((f: any) => f.id),
                );

                const existingSdrIds = new Set(
                  existingFiles
                    .filter((f: any) => f.sourceDataRecordId)
                    .map((f: any) => String(f.sourceDataRecordId)),
                );

                // Also track file names for files that might not have artifact/SDR IDs
                const existingFileNames = new Set(
                  existingFiles
                    .filter((f: any) => f.isFromProject && f.file?.name)
                    .map((f: any) => f.file.name),
                );

                // Filter out documents that already exist
                const newDocuments = allDocuments.filter((doc: any) => {
                  const artifactId = doc.id || doc.artifact_id;

                  if (artifactId) {
                    const artifactFileId = `artifact-${artifactId}`;
                    if (
                      existingArtifactIds.has(String(artifactId)) ||
                      existingFileIds.has(artifactFileId)
                    ) {
                      return false;
                    }
                  }

                  const sdrId = doc.source_data_record_id || doc.sdr_id;
                  if (sdrId && existingSdrIds.has(String(sdrId))) {
                    return false;
                  }

                  if (doc.isFromProject) {
                    let fileName =
                      doc.artifact_name ||
                      doc.file_name ||
                      doc.name ||
                      doc.filename;
                    if (fileName) {
                      fileName = fileName
                        .replace(/^dataset_files\//, "")
                        .replace(/dataset_id_\d+_/, "");
                      if (existingFileNames.has(fileName)) {
                        return false;
                      }
                    }
                  }

                  return true;
                });

                if (newDocuments.length > 0) {
                  // setPrivateDatasetDocumentsAsAttachments is now async
                  await setPrivateDatasetDocumentsAsAttachments(newDocuments);
                }
              }
            })
            .catch((error) => {
              console.error(
                "❌ [useLoadDialogue] Error in artifact fetching process",
                {
                  dialogueId,
                  error: error.message,
                  timestamp: new Date().toISOString(),
                },
              );
            });
        }

        // Set persona from dialogue-config API response if available
        const personaFromConfig = dialogueConfigWithDatasets?.persona as any;
        if (handoff) {
          // The handoff's persona matches the chatConfigId kept above; the
          // store's may since belong to another dialogue.
          setPersona(handoff.persona);
        } else if (personaFromConfig) {
          // Use PersonaConfigurationService to build persona with modelCapabilities
          const personaData =
            PersonaConfigurationService.buildPersonaDataFromAPI(
              personaFromConfig,
            );

          // Add config fields not handled by PersonaConfigurationService
          (personaData as any).config = {
            config_data: personaFromConfig.config_data,
            config_toolkits: personaFromConfig.config_toolkits,
            is_slot_driven: personaFromConfig.is_slot_driven,
            tags: personaFromConfig.tags || [],
          };

          // Override id if not present in personaFromConfig
          if (!personaFromConfig.id && dialogue.config.atom_assist_config_fk) {
            (personaData as any).id = dialogue.config.atom_assist_config_fk;
          }

          // Preserve existing modelCapabilities if API didn't return model_version data
          // This handles the case where the persona was set with capabilities from splash
          // but the dialogue-config API doesn't include model_version in its response
          const existingPersona = useChatStore.getState().persona;
          const hasNewCapabilities =
            personaData.modelCapabilities &&
            Object.keys(personaData.modelCapabilities).length > 0;
          if (
            !hasNewCapabilities &&
            existingPersona?.modelCapabilities &&
            existingPersona.id === personaData.id
          ) {
            personaData.modelCapabilities = existingPersona.modelCapabilities;
          }

          setPersona(personaData);
        } else if (dialogue.persona) {
          // Fallback to dialogue.persona if dialogueConfigWithDatasets doesn't have it
          // Extract modelCapabilities from fallback persona
          let modelCapabilities =
            PersonaConfigurationService.getModelCapabilities(
              dialogue.persona as any,
            );

          const personaId =
            dialogue.config.atom_assist_config_fk || dialogue.persona.id;

          // Preserve existing modelCapabilities if API didn't return model_version data
          const existingPersona = useChatStore.getState().persona;
          const hasNewCapabilities =
            modelCapabilities && Object.keys(modelCapabilities).length > 0;
          if (
            !hasNewCapabilities &&
            existingPersona?.modelCapabilities &&
            existingPersona.id === personaId
          ) {
            modelCapabilities = existingPersona.modelCapabilities;
          }

          const personaData = {
            id: personaId,
            slug: dialogue.persona.slug,
            name: dialogue.persona.name,
            description: dialogue.persona.description || "",
            initialPrompt:
              dialogue.persona.initial_prompt ||
              dialogue.persona.initialPrompt ||
              "",
            systemPrompt:
              dialogue.persona.system_prompt ||
              dialogue.persona.systemPrompt ||
              "",
            datasets: newConfig.datasets || [],
            modelCapabilities,
          };
          setPersona(personaData);
        } else if (initialUserMessage === undefined) {
          // Only clear persona for historical dialogues (page reload/navigation)
          // For fresh dialogues from splash, keep the initial persona set by
          // useSyncPersonaToStore — the dialogue-config API may not yet reflect
          // the routing update (race condition).
          setPersona(null);
        }
      }

      // Restore or clear routing state as soon as dialogue is available,
      // without waiting for dialogueConfigWithDatasets, so stale routing
      // from sessionStorage is cleared immediately on navigation.
      const routingSlug = dialogue.config?.routing_persona_slug;
      if (routingSlug) {
        const personaName = dialogue.persona?.name || routingSlug;
        setRouting({
          persona_slug: routingSlug,
          persona_name: personaName,
          confidence: 0,
          reasoning: "Restored from conversation history",
        });
      } else if (initialUserMessage === undefined) {
        // Clear stale routing when loading a non-routed historical dialogue.
        // Skip for fresh dialogues from splash (including the empty-string
        // auto-start sentinel): the dialogue was fetched
        // before the first message was sent, so its config has no routing
        // slug yet, and this effect re-runs (dialogueConfigWithDatasets
        // resolving) after SSE metadata already set routing. Clearing here
        // would drop the routing chip until a refresh.
        setRouting(null);
      }
    }
    // Force re-apply dialogue config when dialogueConfigWithDatasets updates
    // This ensures the dialogue-config API response is always the final state
  }, [
    dialogueId,
    isDialogueSuccess,
    dialogue?.id,
    dialogue?.config,
    dialogueConfigWithDatasets, // Critical: re-run when this updates
    initialUserMessage,
    // Note: setters are stable references and don't need to be in deps
  ]);

  // Restore a Chat with Document scope, apart from the effect above so that a
  // refetch returning identical data re-runs only this. A fresh dialogue that
  // saved none keeps whatever the entry point set: its first turn has not
  // saved it yet. A started one that saved none has no document scope, so a
  // previous chat's records and chips must not carry into it.
  useEffect(() => {
    if (
      !restoreDocumentScope ||
      !dialogueId ||
      !isDialogueSuccess ||
      !dialogue?.config ||
      !dialogueConfigWithDatasets
    ) {
      return;
    }
    const since = currentDialogue.current?.since ?? 0;
    if (
      dialogueUpdatedAt < since ||
      dialogueConfigUpdatedAt < since ||
      documentScopeAppliedFor.current === dialogueId
    ) {
      return;
    }
    documentScopeAppliedFor.current = dialogueId;

    const documentIds = savedDocumentIds(dialogue.config);
    const hasStarted = (dialogue.messages?.length ?? 0) > 0;
    if (documentIds.length > 0 || hasStarted) {
      setSourceDataRecordIds(documentIds);
      staleDocumentChipIds(
        useChatStore.getState().uploadedFiles,
        documentIds,
      ).forEach(removeUploadedFile);
    }
    if (documentIds.length > 0) {
      fetchDocumentAttachments(documentIds, queryClient).then((chips) => {
        // The user may have moved to another dialogue meanwhile.
        if (useChatStore.getState().activeDialogueId !== dialogueId) return;
        chips.forEach(addUploadedFile);
      });
    }
    // A refetch returning identical data keeps the same reference, so the
    // timestamps are what re-run this once fresh data arrives.
  }, [
    restoreDocumentScope,
    dialogueId,
    isDialogueSuccess,
    dialogue,
    dialogueConfigWithDatasets,
    dialogueUpdatedAt,
    dialogueConfigUpdatedAt,
  ]);

  // Reset loaded dialogue ID and config flag when navigating to a different dialogue
  useEffect(() => {
    const currentDialogueId = shouldEnableDialogueQuery ? dialogueId : null;

    // Reset if we're navigating to a different dialogue
    if (currentDialogueId !== loadedDialogueId.current) {
      loadedDialogueId.current = null;
      hasLoadedConfig.current = false;
    }

    // The dataset-access banner has no server source on load until #30973, so
    // drop the persisted copy as soon as the URL names a different dialogue:
    // history.push navigation (RecentChats, VerificationsPage) never calls
    // resetDialogue, and waiting for the GET would show it against the wrong
    // chat. No id yet (splash -> chat) is resetDialogue's case, not this one.
    const storedAccess = useChatStore.getState().agentDatasetAccess;
    if (
      currentDialogueId !== null &&
      storedAccess &&
      storedAccess.dialogue_id !== currentDialogueId
    ) {
      setAgentDatasetAccess(null);
    }
  }, [dialogueId, shouldEnableDialogueQuery]);

  return {
    dialogue,
    dialogueTree: dialogue?.messages || [],
    isLoading: isDialogueLoading,
    isSuccess: isDialogueSuccess || Boolean(initialUserMessage), // Consider splash navigation as success
    isError: isDialogueError,
    isNotFound,
  };
};

export default useLoadDialogue;
